import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Search,
  Building2,
  UserCircle2,
  Sparkles,
  Trash2,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { API } from "@/admin/AuthContext";
import AdminLayout from "@/admin/AdminLayout";
import PartnerEditor from "@/admin/PartnerEditor";

const TIER = {
  founding: { text: "Fundador", color: "#7E20FF" },
  certified: { text: "Certificado", color: "#208BFF" },
  operator: { text: "Operador", color: "#3ED383" },
};

const TYPE_ICON = { agencia: Building2, consultor: UserCircle2 };

export function AdminPartnersList() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/partners`, {
        withCredentials: true,
      });
      setPartners(data);
    } catch {
      toast.error("Não foi possível carregar parceiros.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (slug) => {
    if (!confirm(`Remover ${slug}? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/partners/${slug}`, { withCredentials: true });
      toast.success("Parceiro removido.");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao remover.");
    }
  };

  const filtered = partners.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [p.name, p.slug, p.city, p.state, p.tagline]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  return (
    <AdminLayout
      title="Parceiros"
      subtitle="Liste, crie, edite e remova perfis do diretório."
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7D8188]"
          />
          <input
            data-testid="admin-partners-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome, slug, cidade…"
            className="w-full rounded-full border border-[#1B1D1D]/12 bg-white pl-10 pr-4 py-2.5 text-sm focus:border-[#7E20FF] focus:outline-none focus:ring-2 focus:ring-[#7E20FF]/15"
          />
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="admin-partners-new"
          className="inline-flex items-center gap-2 rounded-full bg-[#7E20FF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#6818D6] transition-colors"
        >
          <Plus size={14} /> Novo parceiro
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-[#7D8188]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5FAFB] text-[10px] uppercase tracking-[0.18em] text-[#7D8188]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Parceiro</th>
                <th className="text-left px-5 py-3 font-semibold">Tipo</th>
                <th className="text-left px-5 py-3 font-semibold">Tier</th>
                <th className="text-left px-5 py-3 font-semibold">Local</th>
                <th className="text-right px-5 py-3 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1B1D1D]/8">
              {filtered.map((p, idx) => {
                const TypeIcon = TYPE_ICON[p.type] || Building2;
                const tier = TIER[p.tier] || TIER.certified;
                return (
                  <tr key={p.slug} data-testid={`admin-partner-row-${idx}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {p.logo_url ? (
                          <img
                            src={
                              p.logo_url.startsWith("/")
                                ? `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`
                                : p.logo_url
                            }
                            alt=""
                            className="h-9 w-9 rounded-lg object-contain bg-[#F5FAFB] p-1 border border-[#1B1D1D]/8"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-lg bg-[#F5FAFB] border border-[#1B1D1D]/8 grid place-items-center text-[10px] font-bold text-[#7D8188]">
                            {p.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-[#1B1D1D] truncate">
                            {p.name}
                            {p.featured && (
                              <Sparkles
                                size={12}
                                className="inline-block ml-1 text-[#FA9F22]"
                              />
                            )}
                          </div>
                          <div className="text-[11px] text-[#7D8188] truncate">
                            {p.slug}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[#51565F]">
                      <span className="inline-flex items-center gap-1.5">
                        <TypeIcon size={13} /> {p.type}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase"
                        style={{ color: tier.color }}
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: tier.color }}
                        />
                        {tier.text}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#51565F]">
                      {[p.city, p.state].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/admin/parceiros/${p.slug}`}
                          data-testid={`admin-partner-edit-${idx}`}
                          className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#7E20FF] hover:bg-[#7E20FF]/8"
                        >
                          Editar
                        </Link>
                        <a
                          href={`/politics/parceiros/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full p-1.5 text-[#7D8188] hover:text-[#208BFF]"
                          title="Ver público"
                        >
                          <ExternalLink size={14} />
                        </a>
                        <button
                          onClick={() => remove(p.slug)}
                          data-testid={`admin-partner-delete-${idx}`}
                          className="rounded-full p-1.5 text-[#7D8188] hover:text-[#D94A0A]"
                          title="Remover"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-[#7D8188]">
                    Nenhum parceiro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreatePartnerModal onClose={() => { setShowCreate(false); load(); }} />}
    </AdminLayout>
  );
}

function CreatePartnerModal({ onClose }) {
  const [form, setForm] = useState({
    slug: "",
    name: "",
    type: "agencia",
    tier: "certified",
    contact_email: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/partners`,
        { ...form, contact_email: form.contact_email || null },
        { withCredentials: true },
      );
      toast.success("Parceiro criado.");
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao criar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-6">
      <form
        onSubmit={submit}
        data-testid="admin-partner-create-form"
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
      >
        <h3 className="font-display text-xl font-bold text-[#1B1D1D]">
          Novo parceiro
        </h3>
        <p className="mt-1 text-sm text-[#7D8188]">
          Crie o perfil base. Você completa os detalhes na tela de edição.
        </p>
        <div className="mt-5 space-y-3">
          <input
            data-testid="create-input-slug"
            required
            value={form.slug}
            onChange={set("slug")}
            placeholder="slug-unico-em-kebab"
            pattern="^[a-z0-9][a-z0-9\-]*[a-z0-9]$"
            className="w-full rounded-xl border border-[#1B1D1D]/15 px-3.5 py-2.5 text-sm"
          />
          <input
            data-testid="create-input-name"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Nome do parceiro"
            className="w-full rounded-xl border border-[#1B1D1D]/15 px-3.5 py-2.5 text-sm"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              data-testid="create-input-type"
              value={form.type}
              onChange={set("type")}
              className="rounded-xl border border-[#1B1D1D]/15 px-3.5 py-2.5 text-sm"
            >
              <option value="agencia">Agência</option>
              <option value="consultor">Consultor</option>
            </select>
            <select
              data-testid="create-input-tier"
              value={form.tier}
              onChange={set("tier")}
              className="rounded-xl border border-[#1B1D1D]/15 px-3.5 py-2.5 text-sm"
            >
              <option value="founding">Fundador</option>
              <option value="certified">Certificado</option>
              <option value="operator">Operador</option>
            </select>
          </div>
          <input
            data-testid="create-input-email"
            value={form.contact_email}
            onChange={set("contact_email")}
            placeholder="E-mail de contato (= login)"
            type="email"
            className="w-full rounded-xl border border-[#1B1D1D]/15 px-3.5 py-2.5 text-sm"
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-sm font-medium text-[#51565F] hover:text-[#1B1D1D]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="create-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#7E20FF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#6818D6] disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Criar parceiro
          </button>
        </div>
      </form>
    </div>
  );
}

export function AdminPartnerEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  return (
    <AdminLayout
      title="Editar parceiro"
      subtitle={`Você está editando como administrador — todos os campos disponíveis.`}
    >
      <button
        onClick={() => navigate("/admin/parceiros")}
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#51565F] hover:text-[#7E20FF]"
      >
        <ArrowLeft size={14} /> Voltar
      </button>
      <PartnerEditor partnerSlug={slug} asAdmin={true} />
    </AdminLayout>
  );
}
